import { useState } from "react";
import Language from "../../lib/Language";
import Log from "../../lib/Log";
import API from "../../lib/TimeEditAPI";
import Icon from "@timeedit/ui-components/components/Icon/Icon";
import Modal from "@timeedit/ui-components/antd/modal/index";
import NamePromptModal from "./NamePromptModal";
import Menu from "@timeedit/ui-components/antd/menu/index";
import { DeleteOutlined, EditOutlined } from "@timeedit/ui-components/components/Icon/Icons/index";
import { Button } from "@timeedit/ui-components/components/Button/Button";
import { Button as AntButton, Dropdown } from "@timeedit/ui-components/antd";
import type { ItemType } from "@timeedit/ui-components/antd/menu/interface";
import Tooltip from "@timeedit/ui-components/components/Tooltip/Tooltip";
import { TOOLTIP_DELAY, TOPBAR_BUTTON_ICON_HEIGHT } from "../../lib/UIConstants";
import TooltippedTopbarButton from "./TooltippedTopbarButton";
import { TComponent } from "../../types/utils";
import { TReservationLayer } from "../../types/models/TReservationLayer";

type TDraftListProps = {
    layers: TReservationLayer[];
    activeLayer: number;
    setActiveLayer: (layerId: number) => void;
    onLayerCreated: (layerId: number, move: boolean) => void;
    onRemoveLayer: (layerId: number) => void;
    reloadLayers: (callback?: () => void) => void;
    appearance: "button" | "menu";
};

type TDraftEditButtonsProps = {
    layer: TReservationLayer;
    onRename: (layer: TReservationLayer) => void;
    onDelete: (layer: TReservationLayer) => void;
    isEditVisible: boolean;
};

const DraftEditButtons: TComponent<TDraftEditButtonsProps> = ({
    layer,
    onRename,
    onDelete,
    isEditVisible,
}) => {
    return (
        <div
            className="draft-item-actions"
            style={{ flexGrow: "1", alignItems: "right", minWidth: "56px", height: "22px" }}
        >
            {" "}
            {isEditVisible ? (
                <>
                    <Tooltip
                        mouseEnterDelay={TOOLTIP_DELAY}
                        title={Language.get("reservation_list_pref_change_name_title")}
                    >
                        <Button
                            size={"small"}
                            icon={<EditOutlined aria-hidden={true} />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onRename(layer);
                            }}
                        />
                    </Tooltip>
                    <Tooltip mouseEnterDelay={TOOLTIP_DELAY} title={Language.get("dialog_delete")}>
                        <Button
                            style={{ marginLeft: "8px" }}
                            size={"small"}
                            icon={<DeleteOutlined aria-hidden={true} />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(layer);
                            }}
                        />
                    </Tooltip>
                </>
            ) : null}
        </div>
    );
};

type TDraftItemLabelProps = {
    layer: TReservationLayer;
};

const DraftItemLabel: TComponent<TDraftItemLabelProps> = ({ layer }) => <span>{layer.name}</span>;

const DraftList: TComponent<TDraftListProps> = (props) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [renameLayer, setRenameLayer] = useState<TReservationLayer | null>(null);
    const [isNewDraftModalOpen, setIsNewDraftModalOpen] = useState(false);

    const [modal, confirmModalContextHolder] = Modal.useModal();

    const activeLayerName = props.layers.find((lr) => lr.id === props.activeLayer)?.name ?? null;

    const sortedLayers = [...props.layers].sort((a, b) => {
        if (a.id === 1 || b.id === 1) {
            return a.id - b.id;
        }
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
        } else if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
        }
        return 0;
    });

    const createDraft = (name: string) => {
        API.createReservationLayer(name, "", (result, status) => {
            if (!result) {
                if (status) {
                    Log.error(status.details);
                } else {
                    Log.error(Language.get("nc_error_could_not_create_draft"));
                }
            } else {
                props.onLayerCreated(result.id, false);
            }
        });
    };

    const handleRename = (layer: TReservationLayer) => {
        setDropdownOpen(false);
        setRenameLayer(layer);
    };

    const handleDelete = (layer: TReservationLayer) => {
        setDropdownOpen(false);
        modal.confirm({
            icon: <Icon alt={Language.get("nc_draft_confirm_cancel_title")} id="Warning" />,
            title: Language.get("nc_draft_confirm_cancel_title"),
            content: layer.name,
            okText: Language.get("nc_draft_confirm_remove"),
            cancelText: Language.get("nc_draft_dont_remove"),
            onOk: () => {
                props.onRemoveLayer(layer.id);
            },
        });
    };

    const noDraftSelected = props.activeLayer === 0;

    const [hoveredLayerId, setHoverLayerId] = useState(0);

    const innerNavigation = [
        ...sortedLayers.map((layer) => ({
            key: layer.id,
            extra: (
                <DraftEditButtons
                    isEditVisible={hoveredLayerId === layer.id}
                    layer={layer}
                    onRename={handleRename}
                    onDelete={handleDelete}
                />
            ),
            label: <DraftItemLabel layer={layer} />,
            onClick: () => {
                props.setActiveLayer(layer.id);
            },
            onMouseEnter: () => {
                setHoverLayerId(layer.id);
            },
            onMouseLeave: () => {
                setHoverLayerId(0);
            },
        })),
        { type: "divider" },
        {
            key: "newDraft",
            label: Language.get("nc_new_draft"),
            onClick: (event) => {
                event.domEvent.preventDefault();
                setDropdownOpen(false);
                setIsNewDraftModalOpen(true);
            },
        },
    ] as ItemType[];

    const performRename = (newName: string) => {
        if (renameLayer) {
            API.renameReservationLayer(renameLayer.id, newName, renameLayer.description, () => {
                props.reloadLayers();
            });
        }
        setRenameLayer(null);
    };

    const renameModal = (
        <NamePromptModal
            open={renameLayer !== null}
            title={Language.get("cal_header_popup_period_change_name")}
            onSaveName={performRename}
            oldName={renameLayer?.name ?? ""}
            onCancel={() => {
                setRenameLayer(null);
            }}
        />
    );

    const newDraftModal = (
        <NamePromptModal
            open={isNewDraftModalOpen}
            title={Language.get("nc_new_draft")}
            onSaveName={(newName) => {
                createDraft(newName);
                setIsNewDraftModalOpen(false);
            }}
            onCancel={() => {
                setIsNewDraftModalOpen(false);
            }}
        />
    );

    const buttonElement =
        props.appearance === "menu" ? (
            <AntButton
                type="text"
                className="user-dropdown-trigger-button"
                style={{ border: "0" }}
                onClick={(e) => {
                    e.preventDefault();
                }}
            >
                {noDraftSelected
                    ? Language.get("nc_draft_list_no_draft")
                    : Language.get("nc_draft_menu_title", activeLayerName)}
                <Icon
                    id="DownOutlined"
                    style={{ fontSize: 16 }}
                    alt={Language.get("nc_drafts_title")}
                />
            </AntButton>
        ) : (
            <TooltippedTopbarButton
                tooltip={Language.get("nc_drafts_title")}
                icon={
                    <Icon
                        id="AuditOutlined"
                        size={TOPBAR_BUTTON_ICON_HEIGHT}
                        alt={Language.get("nc_drafts_title")}
                    />
                }
                onClick={() => {}}
            />
        );

    return (
        <>
            <Dropdown
                trigger={["click"]}
                open={dropdownOpen}
                onOpenChange={setDropdownOpen}
                destroyOnHidden
                className={`tw:h-full
        tw:flex tw:items-center tw:justify-center tw:overflow-hidden
        tw:text-inherit tw:leading-[0]
      `}
                popupRender={() => (
                    <Menu
                        items={innerNavigation}
                        selectedKeys={[`${props.activeLayer}`]}
                        selectable
                        mode="vertical"
                    />
                )}
            >
                {buttonElement}
            </Dropdown>
            {renameModal}
            {newDraftModal}
            {confirmModalContextHolder}
        </>
    );
};

export default DraftList;
